function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
    if(this.input.value == ''){
      this.input.value = this.input.min || 1;
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });

    const cssHref = window.productStyleSheet;
    if (cssHref) {
      button.addEventListener('mouseenter', () => {
        if (!document.querySelector(`link[rel="preload"][href="${cssHref}"]`)) {
          const link = document.createElement('link');
          link.rel = 'preload';
          link.as = 'style';
          link.href = cssHref;
          document.head.appendChild(link);
        }
      });
    }
  }
}
customElements.define('modal-opener', ModalOpener);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}
customElements.define('product-recommendations', ProductRecommendations);

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}

function setHeaderHeight() {
  const header = document.querySelector(".header-main");
  if (!header) return;
  const value = header.getBoundingClientRect().height;
  const currentValue = getComputedStyle(
    document.documentElement
  ).getPropertyValue("--header-height");

  if (value && value != currentValue) {
    document.documentElement.style.setProperty("--header-height", `${value}px`);
  }
}

function setHeaderTop() {
  const header = document.querySelector(".header-main");
  if (!header) return;

  const value = header.getBoundingClientRect().top;
  let currentValue = getComputedStyle(document.documentElement).getPropertyValue("--header-top-position").trim();

  // Handle empty value or convert to number
  const currentNumeric = parseFloat(currentValue || NaN);

  if (!Number.isFinite(currentNumeric) || currentNumeric !== value) {
    document.documentElement.style.setProperty("--header-top-position", `${value}px`);
  }
}

function setHeaderBottom() {
  const header = document.querySelector(".header-main");
  if (!header) return;
  const value = header.getBoundingClientRect().bottom;
  const currentValue = getComputedStyle(
    document.documentElement
  ).getPropertyValue("--header-bottom-position");

  if (value && value != currentValue) {
    document.documentElement.style.setProperty("--header-bottom-position", `${value}px`);
  }
}

function setPromoHeight() {
  const promo = document.querySelector(".announcement-bar");
  if (!promo) return;
  const value = promo.getBoundingClientRect().height;
  const currentValue = getComputedStyle(
    document.documentElement
  ).getPropertyValue("--promo-height");

  if (value && value != currentValue) {
    document.documentElement.style.setProperty("--promo-height", `${value}px`);
  }
}

function setCustomPropertiesOnLoad() {
  setHeaderHeight();
  setPromoHeight();
  setHeaderTop();
  setHeaderBottom();
}

function setCustomPropertiesOnScroll() {
  setHeaderTop();
  setHeaderBottom();
}

function setCustomPropertiesOnResize() {
  setHeaderHeight();
  setPromoHeight();
  setHeaderTop();
  setHeaderBottom();
}

window.addEventListener("load", setCustomPropertiesOnLoad, false);

window.addEventListener("scroll", debounce(setCustomPropertiesOnScroll, 300), false);

window.addEventListener("resize", debounce(setCustomPropertiesOnResize, 300), false);

class HeaderMega extends HTMLElement {
  constructor() {
    super();

    this.dropdowns = this.querySelectorAll(".header-dropdown");
    this.dropdownToggles = this.querySelectorAll(".header-dropdown-toggle");
    this.dropdownMenus = this.querySelectorAll(".header-dropdown-menu");
    this.menuTrigger = this.getAttribute("trigger");

    this._openTimers = new Map();
    this._closeTimers = new Map();
    this.hoverDelay = 100;

    this.bindEvents();
  }

  bindEvents() {
    this.addEventListener("keyup", this.onKeyUp.bind(this));
    this.addEventListener("focusout", this.onDropdownFocusOut.bind(this));

    this.bindClickEvents();
    if (this.menuTrigger === "hover") {
      this.bindHoverEvents();
    }
  }

  /** CLICK EVENTS **/
  bindClickEvents() {
    this.dropdownToggles.forEach((item) => {
      item.addEventListener("click", this.onDropdownToggleClick.bind(this));
    });
  }

  /** HOVER EVENTS **/
  bindHoverEvents() {
    this.dropdownToggles.forEach((toggle) => {
      const dropdown = toggle.parentNode;
      const menu = dropdown.querySelector(".header-dropdown-menu");

      // hover in/out for toggle
      toggle.addEventListener("mouseenter", () =>
        this.handleHoverEnter(dropdown)
      );
      toggle.addEventListener("mouseleave", () =>
        this.handleHoverLeave(dropdown)
      );

      // hover in/out for dropdown menu
      if (menu) {
        menu.addEventListener("mouseenter", () =>
          this.handleHoverEnter(dropdown)
        );
        menu.addEventListener("mouseleave", () =>
          this.handleHoverLeave(dropdown)
        );
      }
    });
  }

  handleHoverEnter(dropdown) {
    clearTimeout(this._closeTimers.get(dropdown));
    const timer = setTimeout(() => {
      this.openDropdown(dropdown);
      this.openMega();
    }, this.hoverDelay);
    this._openTimers.set(dropdown, timer);
  }

  handleHoverLeave(dropdown) {
    clearTimeout(this._openTimers.get(dropdown));
    const timer = setTimeout(() => {
      this.closeDropdown(dropdown);
      this.closeMega();
    }, this.hoverDelay);
    this._closeTimers.set(dropdown, timer);
  }

  onDropdownToggleClick(event) {
    event.preventDefault();

    const dropdownToggle = event.currentTarget;
    const dropdown = dropdownToggle.parentNode;

    if (this.isDropdownOpen(dropdown)) {
      this.closeDropdown(dropdown);
      this.closeMega();
    } else {
      this.openDropdown(dropdown);
      this.openMega();
    }
  }

  onDropdownFocusOut(event) {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      if (this.hasDropdownOpen()) {
        this.closeAllDropdowns();
        this.closeMega();
      }
    }
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== "ESCAPE") return;

    const openDetailsElement = event.target.closest("details[open]");
    if (!openDetailsElement) return;

    this.closeDropdown(openDetailsElement);
    this.closeMega();
  }

  isDropdownOpen(detailsElement) {
    return (
      detailsElement.hasAttribute("open")
    );
  }

  isDropdownClose(detailsElement) {
    return !detailsElement.hasAttribute("open");
  }

  hasDropdownOpen() {
    return this.classList.contains("dropdown-open");
  }

  openDropdown(detailsElement) {
    const summaryElement = detailsElement.querySelector("summary");

    // close all sub drawers
    this.closeAllDropdowns();

    detailsElement.setAttribute("open", "");
    summaryElement.setAttribute("aria-expanded", true);
    summaryElement.classList.add("active");
    this.classList.add("dropdown-open");
    trapFocus(detailsElement, summaryElement);
  }

  closeDropdown(detailsElement) {
    const summaryElement = detailsElement.querySelector("summary");
    detailsElement.removeAttribute("open");
    summaryElement.setAttribute("aria-expanded", false);
    summaryElement.classList.remove("active");
    removeTrapFocus(summaryElement);
    this.classList.remove("dropdown-open");
  }

  closeAllDropdowns() {
    this.dropdowns.forEach((item) => {
      if (this.isDropdownOpen(item)) {
        this.closeDropdown(item);
      }
    });
  }

  openMega() {
    document.body.classList.add("header-mega-open", "overflow-hidden");
  }

  closeMega() {
    document.body.classList.remove("header-mega-open", "overflow-hidden");
  }
}
customElements.define("header-mega", HeaderMega);

class FooterNewsletter extends HTMLElement {
  constructor() {
      super();
      this.sectionId = this.dataset.sectionId;
  }

  connectedCallback() {
      this.input = this.querySelector("#footer-newsletter-input");
      this.form = this.querySelector("form");
      this.invalidMessage = this.querySelector(".invalid-email");
      this.emptyMessage = this.querySelector(".empty-email");
      this.klaviyoListId = this.form.dataset.kalviyoListId || "";
      this.emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      this.label = this.querySelector(".footer-input-label");
      this.submitButton = this.querySelector(".newsletter-button");
      
      if (this.form) {
        this.form.addEventListener("submit", (e) => this.handleSubmit(e));
        this.input.addEventListener("input", () => {
          if (this.input.value.trim()) {
            this.showNewsletterMessage(null);
          }
        });
      }
  }

  showNewsletterMessage(messageElement) {
      this.emptyMessage.classList.add("hidden");
      this.invalidMessage.classList.add("hidden");
      if (messageElement) messageElement.classList.remove("hidden");
  }

  validateNewsletterEmail() {
      const email = this.input.value.trim();
      if (!email) return this.showNewsletterMessage(this.emptyMessage), false;
      if (!this.emailPattern.test(email)) return this.showNewsletterMessage(this.invalidMessage), false;
      return this.showNewsletterMessage(null), true;
  }

  async subscribeToNewsletter(email) {
      try {
          const response = await fetch("https://a.klaviyo.com/client/subscriptions/?company_id=SGjG87", {
              method: "POST",
              headers: {
                  accept: "application/vnd.api+json",
                  revision: "2025-01-15",
                  "content-type": "application/vnd.api+json"
              },
              body: JSON.stringify({
                  data: {
                      type: "subscription",
                      attributes: {
                          custom_source: "Footer Form",
                          profile: {
                              data: {
                                  type: "profile",
                                  attributes: {
                                      email,
                                      subscriptions: { email: { marketing: { consent: "SUBSCRIBED" } } }
                                  }
                              }
                          }
                      },
                      relationships: { list: { data: { type: "list", id: this.klaviyoListId } } }
                  }
              })
          });
          console.log("Klaviyo subscription response:", response);
          if (response.status === 202) {
              this.input.placeholder = window.footerStrings.subscribed;
              this.input.value = "";
              this.form.setAttribute("data-submitted", "");
              this.form.setAttribute("inert", "");
              this.submitButton.style.display = "none";
          } else {
              throw new Error("Failed to subscribe");
          }
      } catch (error) {
        console.log("Klaviyo subscription error:", error);
          this.invalidMessage.querySelector('span').textContent = "Subscription failed. Please try again.";
          this.showNewsletterMessage(this.invalidMessage);
      }
  }

  handleSubmit(event) {
      event.preventDefault();
      if (!this.validateNewsletterEmail()) return;
      this.subscribeToNewsletter(this.input.value.trim());
  }
}
customElements.define("footer-newsletter", FooterNewsletter);

class MediaControls extends HTMLElement {
  constructor() {
    super();
    this.handleMediaToggle = this.handleMediaToggle.bind(this);
    this.handleMediaVolume = this.handleMediaVolume.bind(this);
    this.handleVideoEnd = this.handleVideoEnd.bind(this);
    this.handleAutoPlay = this.handleAutoPlay.bind(this);
  }

  connectedCallback() {
    this.addEventListener("click", this.handleClick);
    // this.addEventListener("keydown", this.handleKeyDown);
    this.closest('.video-banner').querySelectorAll("video").forEach((video) => {
      if (!video.hasAttribute("loop")) {
        video.addEventListener("ended", this.handleVideoEnd);
      }
    });
    this.handleAutoPlay();
  }

  handleClick(event) {
    const button = event.target.closest("button");

    if (!button) return;

    if (button.classList.contains("media-toggle")) {
      this.handleMediaToggle(button);
    } else if (button.classList.contains("volume-toggle")) {
      this.handleMediaVolume(button);
    }
  }

  handleKeyDown(event) {
    if (event.key === "Enter") {
      this.handleClick(event);
    }
  }

  handleMediaToggle(button) {
    let mediaContainer = this.closest(".video-banner");
    if (!mediaContainer) return;

    let videos = mediaContainer.querySelectorAll("video");
    let video = videos.length > 1 && window.matchMedia("(max-width: 768px)").matches ? videos[1] : videos[0];

    if (!video) return;

    if (video.paused) {
      video.play();
      button.classList.remove('paused');
    } else {
      video.pause();
      button.classList.add('paused');
    }
  }

  handleMediaVolume(button) {
    let mediaContainer = this.closest(".video-banner");
    if (!mediaContainer) return;

    let videos = mediaContainer.querySelectorAll("video");
    let video = videos.length > 1 && window.matchMedia("(max-width: 768px)").matches ? videos[1] : videos[0];

    if (!video) return;

    video.muted = !video.muted;

    button.classList.toggle("unmuted", !video.muted);
  }

  handleVideoEnd() {
    let toggleButton = this.querySelector(".media-toggle");
    if (toggleButton) {
      toggleButton.classList.add('paused');
    }
  }

  handleAutoPlay() {
    let mediaContainer = this.querySelector(".video-banner");
    if (!mediaContainer) return;

    let videos = mediaContainer.querySelectorAll("video");
    let video = videos.length > 1 && window.matchMedia("(max-width: 768px)").matches ? videos[1] : videos[0];

    if (!video) return;

    if(mediaContainer.dataset.autoplay === "true") {
      video.play();
    }
  }

}
customElements.define("media-controls", MediaControls);

class CollectionTabs extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.tabButtons = this.querySelectorAll('.ct-tab-button');
    this.tabContents = this.querySelectorAll('.ct-tab-content');
    this.tabControls = this.querySelectorAll('.ct-controls');

    this.tabButtons.forEach(button => {
      button.addEventListener('click', () => this.openTab(button));
    });

    // Optionally activate first tab
    if (this.tabButtons.length > 0) {
      this.openTab(this.tabButtons[0]);
    }
  }

  openTab(button) {
    const targetId = button.getAttribute('data-tab');

    // Hide all tab contents
    this.tabContents.forEach(content => {
      content.classList.remove('active');
    });
    this.tabControls.forEach(control => {
      control.classList.remove('active');
    });

    // Remove active class from all buttons
    this.tabButtons.forEach(btn => {
      btn.classList.remove('active');
    });

    // Show the selected content
    const targetContent = this.querySelector(`#${targetId}`);
    if (targetContent) {
      targetContent.classList.add('active');
    }

    const targetControls = this.querySelector(`[data-controls='${targetId}']`);
    if (targetControls) {
      targetControls.classList.add('active');
    }

    // Add active class to clicked button
    button.classList.add('active');
    let color_accent = button.getAttribute('data-color-accent');
    let text_color = button.getAttribute('data-text-color');
    let subnav_text_color = button.getAttribute('data-subnav');
    if (color_accent) {
      this.querySelector(".ct-header").style.setProperty('--color-primary', color_accent);
    }
    if (text_color) {
      this.querySelector(".ct-header").style.setProperty('--_color-button', text_color);
    }
    if (subnav_text_color) {
      this.querySelector(".ct-header").style.setProperty('--_sub_nav-active-color', subnav_text_color);
    }
  }
}
customElements.define('collection-tabs', CollectionTabs);

class collectionTabsSlider extends HTMLElement {
  constructor() {
      super();
  }
  connectedCallback() {
      const parentId = this.parentElement.id;
      const prevButton = document.querySelector(`[data-controls='${parentId}'] .slider-control-prev`);
      const nextButton = document.querySelector(`[data-controls='${parentId}'] .slider-control-next`);
    
      this.swiper = new Swiper(this, {
        slidesPerView: 1.47,
        spaceBetween: 10,
        freeMode: true,
        grabCursor: true,
        mousewheel: {
            forceToAxis: true
        },
        navigation: {
            nextEl: nextButton,
            prevEl: prevButton,
        },
        breakpoints: {
            768: {
                slidesPerView: 2.2,
            },
            825: {
                slidesPerView: 3,
            },
            1024: {
                slidesPerView: 4,
            },
            2000: {
                slidesPerView: 5,
            }
        },
      });
  }
}
customElements.define("ct-slider", collectionTabsSlider);

class cardsSlider extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
      const parentId = this.parentElement.id;
      const prevButton = document.querySelector(`[data-controls='${parentId}'] .slider-control-prev`);
      const nextButton = document.querySelector(`[data-controls='${parentId}'] .slider-control-next`);
      const thumbs = this.closest('.cards-slider-inner').querySelectorAll('.cs-tab-button');
    
      this.swiper = new Swiper(this, {
        slidesPerView: "auto",
        spaceBetween: 10,
        grabCursor: true,
        mousewheel: {
            forceToAxis: true
        },
        navigation: {
            nextEl: nextButton,
            prevEl: prevButton,
        },
        observers: true,
        observeParents: true,
        observeSlideChildren: true,
        on: {
          slideChange: function () {
            if (window.innerWidth <= 500) {
              setTimeout(() => {    
                const prevSlide = this.slides[this.previousIndex];
                const prevCard = prevSlide?.querySelector(".cs-card");
    
                if (prevCard?.classList.contains("open")) {
                  prevCard.toggleCard();
                }
              }, 201);
            }
          }
        }

      });

      thumbs.forEach((thumb) => {
        thumb.addEventListener('click', (event) => {
          event.preventDefault();

          const targetId = thumb.getAttribute('data-tab');
          const targetSlide = this.querySelector(`#${targetId}`);
          const allSlides = this.querySelectorAll('.swiper-slide');

          if (!targetSlide) return;

          // Move swiper to the target slide
          const slideIndex = Array.from(allSlides).indexOf(targetSlide);
          this.swiper.slideTo(slideIndex);
          
          if(window.innerWidth <= 768) {
            const activeCard = targetSlide.querySelector('.cs-card');
            activeCard.setHeight();
            activeCard.toggleCard();
          }
        });
      });
  }
}
customElements.define("cs-slider", cardsSlider);

class cardsSliderCard extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.trigger = this.querySelector('.cs-trigger');
    this.description = this.querySelector('.cs-card-description');
    this.button = this.querySelector('.cs-card_cta-secondary');

    this.trigger.addEventListener('click', () => this.toggleCard());
    this.setHeight();
  }

  setHeight() {
    this.parentElement.style.setProperty('--card-height', `${this.offsetHeight}px`);
  }

  toggleCard() {
    const isOpen = this.classList.contains('open');
    const allCards = document.querySelectorAll('.cs-card');
    const allNav = [...this.closest('.cards-slider-inner').querySelectorAll('.cs-tab-button')];
    const trigger = this.trigger;

    const descMaxHeight = !isOpen ? `${this.description.scrollHeight}px` : null;

    if (descMaxHeight !== null) {
      this.description.style.setProperty('--max-height', descMaxHeight);
    } else {
      this.description.style.removeProperty('--max-height', 0);
    }

    allCards.forEach(card => {
      if (card !== this) {
        card.classList.remove('open');
        card.description.style.setProperty('--max-height', 0);
        if(window.matchMedia("(max-width: 768px)").matches);
        let updateDelay = window.matchMedia("(max-width: 768px)").matches ? 401 : 201;

        setTimeout(() => {
          this.closest('.swiper-wrapper').style.transitionDuration = '200ms';
          this.closest('cs-slider').swiper.update();
        }, updateDelay);
      }
    });

    allNav.forEach(nav => {
      if (nav !== trigger) {
        nav.classList.remove('active');
      }
    });

    trigger.setAttribute('aria-expanded', !isOpen);
    trigger.classList.toggle('active', !isOpen);
    this.classList.toggle('open', !isOpen);
    allNav.filter(nav => nav.getAttribute('data-tab') === this.parentElement.id).forEach(nav => {
      nav.classList.toggle('active', !isOpen);
    });

    console.log('isOpen', isOpen);
    if (!isOpen && window.innerWidth >= 769) {
      const swiper = this.closest('cs-slider').swiper;
      const slide = this.closest('.swiper-slide'); // parent slide
      const allSlides = [...slide.parentElement.children]; // all .swiper-slide
      const index = allSlides.indexOf(slide);

      if (index !== -1) {
        setTimeout(() => {
          swiper.slideTo(index);
        }, 401);
      }
    }
    
  }
}
customElements.define('cs-card', cardsSliderCard);

class IngredientsTabs extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.tabButtons = this.querySelectorAll('.it-content-tab-button');
    this.tabContents = this.querySelectorAll('.it-content-tab');
    this.tabImages = this.querySelectorAll('[tab-media]');
    this.dynamicTextCTA = this.querySelector('[data-dynamic-text-update]');
    this.contentBottom = this.querySelector('.it-content-bottom');
    this.activeImage = null;

    // Set max height for mobile
    this.setMaxTabHeight();

    window.addEventListener('resize', () => {
      this.setMaxTabHeight();
    });

    // Hide all banners initially
    this.tabImages.forEach(img => img.style.display = 'none');

    this.tabButtons.forEach(button => {
      button.addEventListener('click', () => this.openTab(button));
    });

    if (this.tabButtons.length > 0) {
      this.openTab(this.tabButtons[0], true);
    }
  }

  setMaxTabHeight() {
    if (!this.contentBottom) return;

    let maxHeight = 0;

    this.tabContents.forEach(tab => {
      const originalDisplay = tab.style.display;

      // temporarily show hidden tabs to measure height
      tab.style.display = 'block';

      const height = tab.offsetHeight;
      maxHeight = Math.max(maxHeight, height);

      tab.style.display = originalDisplay;
    });

    this.contentBottom.style.setProperty(
      '--content-height',
      `${maxHeight}px`
    );
  }

  openTab(button, isFirstLoad = false) {
    const targetId = button.getAttribute('data-tab');

    this.tabContents.forEach(content => {
      content.style.display = 'none';
    });

    this.tabButtons.forEach(btn => {
      btn.classList.remove('active');
    });

    const targetContent = this.querySelector(`#${targetId}`);
    if (targetContent) {
      targetContent.style.display = 'block';
    }

    if(this.dynamicTextCTA) {
      const dynamicText = this.dynamicTextCTA.dataset.dynamicTextUpdate + ' ' + button.getAttribute('data-dynamic-text');
      this.dynamicTextCTA.textContent = dynamicText;
    }

    const targetImage = this.querySelector(`[tab-media='${targetId}']`);

    if (targetImage) {
      this.tabImages.forEach(img => img.style.display = 'none');
      targetImage.style.display = 'block';
      this.activeImage = targetImage;
    } else if (isFirstLoad && !this.activeImage) {
      if (this.tabImages.length > 0) {
        this.tabImages[0].style.display = 'block';
        this.activeImage = this.tabImages[0];
      }
    }

    button.classList.add('active');
  }
}

customElements.define('ingredients-tabs', IngredientsTabs);

class NewsletterPopup extends HTMLElement {
  constructor() {
      super();
      this.newsletterClosedKey = "newsletterPopupClosed";
      this.newsletterExpiryKey = "newsletterPopupExpiry";
      this.newsletterExpiryTime = 7 * 24 * 60 * 60 * 1000; // 7 days
      this.sectionId = this.dataset.sectionId;
  }

  connectedCallback() {
      this.newsletterInput = this.querySelector("#popup-newsletter-input");
      this.closeBtn = this.querySelector(".newsletter-close");
      this.newsletterForm = this.querySelector("form");
      this.newsletterInvalidMessage = this.querySelector(".invalid-email");
      this.newsletterEmptyMessage = this.querySelector(".empty-email");
      this.newsletterKlaviyoListID = this.newsletterForm.dataset.kalviyoListId || "";
      this.newsletterEmailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      this.openDelay = this.dataset.openDelay || 20000;
      this.closeDelay = this.dataset.closeDelay || 10000;
      this.hideOnSubmit = this.querySelectorAll(".hide_on_submit");
      this.successMessage = this.querySelector(".newsletter_success_message");
      
      if(!Shopify.designMode){this.open();}
      this.closeBtn.addEventListener("click", () => this.close());
      if (this.newsletterForm) {
        this.newsletterForm.addEventListener("submit", (e) => this.handleSubmit(e));
        this.newsletterInput.addEventListener("input", () => {
          if (this.newsletterInput.value.trim()) {
            this.showNewsletterMessage(null);
          }
        });
      }
      if(Shopify.designMode){
        this.shopifyListeners();
      }
  }

  shopifyListeners() {
    document.addEventListener('shopify:section:select', (event) => {
        if (event.detail.sectionId === this.sectionId) {
            this.openInEditor();
        }else{
          this.closeInEditor();
        }
    });
    document.addEventListener('shopify:section:load', (event) => {
        if (event.detail.sectionId === this.sectionId) {
            this.openInEditor();
        }else{
          this.closeInEditor();
        }
    });
  }

  open() {
      const storedExpiry = localStorage.getItem(this.newsletterExpiryKey);
      const now = Date.now();
      
      if (!storedExpiry || now > storedExpiry) {
        setTimeout(() => {
              this.classList.add("open");
              this.removeAttribute("inert");
              document.body.classList.add("newsletter-shown");
              setTimeout(() => {
                  trapFocus(this,this.closeBtn );
              }, 210);
          }, this.openDelay);
      }
  }

  close() {
      this.classList.remove("open");
      document.body.classList.remove("newsletter-shown");
      localStorage.setItem(this.newsletterClosedKey, "true");
      localStorage.setItem(this.newsletterExpiryKey, Date.now() + this.newsletterExpiryTime);
      this.setAttribute("inert", "");
  }

  showNewsletterMessage(messageElement) {
      this.newsletterEmptyMessage.classList.add("hidden");
      this.newsletterInvalidMessage.classList.add("hidden");
      if (messageElement) messageElement.classList.remove("hidden");
  }

  validateNewsletterEmail() {
      const email = this.newsletterInput.value.trim();
      if (!email) return this.showNewsletterMessage(this.newsletterEmptyMessage), false;
      if (!this.newsletterEmailPattern.test(email)) return this.showNewsletterMessage(this.newsletterInvalidMessage), false;
      return this.showNewsletterMessage(null), true;
  }

  async subscribeToNewsletter(email) {
      try {
          const response = await fetch("https://a.klaviyo.com/client/subscriptions/?company_id=SGjG87", {
              method: "POST",
              headers: {
                  accept: "application/vnd.api+json",
                  revision: "2025-01-15",
                  "content-type": "application/vnd.api+json"
              },
              body: JSON.stringify({
                  data: {
                      type: "subscription",
                      attributes: {
                          custom_source: "Newsletter Popup",
                          profile: {
                              data: {
                                  type: "profile",
                                  attributes: {
                                      email,
                                      subscriptions: { email: { marketing: { consent: "SUBSCRIBED" } } }
                                  }
                              }
                          }
                      },
                      relationships: { list: { data: { type: "list", id: this.newsletterKlaviyoListID } } }
                  }
              })
          });
          if (response.status === 202) {
              this.newsletterInput.placeholder = this.newsletterForm.dataset.successMessage || "Thank you for subscribing!";
              this.hideOnSubmit.forEach((item) => {
                item.classList.add("hidden");
              });
              this.successMessage.classList.remove("hidden");
              this.newsletterForm.setAttribute("inert", "");
              this.newsletterForm.setAttribute("data-submitted", "");
              this.closeBtn.focus();
              setTimeout(() => {
                this.close();
              }, this.closeDelay);
          } else {
              throw new Error("Failed to subscribe");
          }
      } catch (error) {
        console.log("Klaviyo subscription error:", error);
          this.newsletterInvalidMessage.querySelector('span').textContent = "Subscription failed. Please try again.";
          this.showNewsletterMessage(this.newsletterInvalidMessage);
      }
  }

  openInEditor(){
    const sidebar = document.querySelector("#sidebar");
    const isSidebarOpen = sidebar && sidebar.classList.contains("open");
    const cartDrawer = document.querySelector("cart-drawer");
    const sizeGuideModal = document.querySelector(".size_guide_modal");
    if(sizeGuideModal && sizeGuideModal.getState() === "open") {
      sizeGuideModal.close();
    }
    if(isSidebarOpen){
      closeSidebar();
    }
    cartDrawer.close();

    this.classList.add("open");
    this.removeAttribute("inert");
    setTimeout(() => {
        trapFocus(this,this.newsletterInput );
    }, 110);
  }

  closeInEditor(){
    this.classList.remove("open");
    this.setAttribute("inert", "");
  }

  handleSubmit(event) {
      event.preventDefault();
      if (!this.validateNewsletterEmail()) return;
      this.subscribeToNewsletter(this.newsletterInput.value.trim());
  }
}
customElements.define("newsletter-popup", NewsletterPopup);

class videoCardSlider extends HTMLElement {
  constructor() {
    super();
    this.observer = null;
  }

  connectedCallback() {
    this.swiper = new Swiper(this, {
      slidesPerView: 1.5,
      spaceBetween: 10,
      mousewheel: {
        forceToAxis: true,
      },
      freeMode: true,
      navigation: {
        nextEl: '.vs-next',
        prevEl: '.vs-prev',
      },
      breakpoints: {
        768: {
          slidesPerView: 2,
          spaceBetween: 20,
        },
        1600: {
          slidesPerView: 3,
          spaceBetween: 20,
        },
      },
      // Add slideChange event to handle video pausing
      on: {
        slideChange: () => {
          this.pauseVideosOutOfView();
        },
      },
    });

    // Setup IntersectionObserver for visibility detection
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) {
            const video = entry.target.querySelector('video');
            const mediaControls = entry.target.querySelector('media-controls');
            if (video && mediaControls && !video.paused) {
              mediaControls.handleMediaToggle(mediaControls.querySelector('.media-toggle'));
            }
          }
        });
      },
      {
        threshold: 0.1, // Trigger when less than 10% of the slide is visible
      }
    );

    // Add video controls functionality
    this.querySelectorAll('.swiper-slide').forEach(slide => {
      const video = slide.querySelector('video');
      const mediaControls = slide.querySelector('media-controls');
      
      // Video play/pause toggle
      video.addEventListener('click', () => {
        mediaControls?.handleMediaToggle(mediaControls.querySelector('.media-toggle'));
      });

      // Add play event listener to pause other videos
      video.addEventListener('play', () => {
        this.pauseOtherVideos(video);
      });

      // Observe slide for visibility changes
      if (video && mediaControls) {
        this.observer.observe(slide);
      }
    });
  }

  disconnectedCallback() {
    // Cleanup observer when component is removed
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  pauseVideosOutOfView() {
    const slides = this.swiper.slides;
    const activeIndex = this.swiper.activeIndex;
    const slidesPerView = this.swiper.params.slidesPerView;

    slides.forEach((slide, index) => {
      const video = slide.querySelector('video');
      const mediaControls = slide.querySelector('media-controls');
      if (video && mediaControls) {
        const isVisible = index >= activeIndex && index < activeIndex + slidesPerView;
        if (!isVisible && !video.paused) {
          mediaControls.handleMediaToggle(mediaControls.querySelector('.media-toggle'));
        }
      }
    });
  }

  pauseOtherVideos(currentVideo) {
    this.querySelectorAll('.swiper-slide').forEach(slide => {
      const video = slide.querySelector('video');
      const mediaControls = slide.querySelector('media-controls');
      if (video && mediaControls && video !== currentVideo && !video.paused) {
        mediaControls.handleMediaToggle(mediaControls.querySelector('.media-toggle'));
      }
    });
  }
}
customElements.define('video-card-slider', videoCardSlider);

class productGalleryGrid extends HTMLElement {
  constructor() {
    super();
    this.videoItems = [];
  }

  connectedCallback() {
    const items = this.querySelectorAll('.product-gallery-item.has_video');

    items.forEach((item) => {
      const video = item.querySelector('video');
      const videoToggle = item.querySelector('.gallery-video-toggle');
      const videoSeekbar = item.querySelector('.gallery-video-seekbar');
      const volumeToggle = item.querySelector('.gallery-volume-toggle');

      if (!video || !videoToggle || !videoSeekbar || !volumeToggle) return;

      const itemData = { item, video, videoToggle, videoSeekbar, volumeToggle };
      this.videoItems.push(itemData);
      this.bindEvents(itemData);
    });

    this.startSeekbarLoop();
  }

  bindEvents({ video, videoToggle, videoSeekbar, volumeToggle }) {
    videoToggle.addEventListener('click', () => this.togglePlayback(video, videoToggle));
    video.addEventListener('click', () => this.togglePlayback(video, videoToggle));
    video.addEventListener('ended', () => this.resetPlaybackButton(videoToggle));

    videoSeekbar.addEventListener('input', () =>
      this.seekVideo(video, videoSeekbar)
    );

    volumeToggle.addEventListener('click', () => {
      video.muted = !video.muted;
      volumeToggle.classList.toggle('muted', video.muted);
    });
  }

  togglePlayback(video, button) {
    if (video.paused || video.ended) {
      video.play();
      button.textContent = 'Pause';
    } else {
      video.pause();
      button.textContent = 'Play';
    }
  }

  resetPlaybackButton(button) {
    button.textContent = 'Play';
  }

  seekVideo(video, seekbar) {
    const time = (seekbar.value / 100) * video.duration;
    video.currentTime = time;
    this.updateSeekbar(video, seekbar);
  }

  updateSeekbar(video, seekbar) {
    const progress = (video.currentTime / video.duration) * 100;
    seekbar.value = progress;
    seekbar.style.setProperty('--progress', `${progress}%`);
  }

  startSeekbarLoop() {
    const loop = () => {
      this.videoItems.forEach(({ video, videoSeekbar }) => {
        if (!video.paused && !video.ended) {
          this.updateSeekbar(video, videoSeekbar);
        }
      });
      requestAnimationFrame(loop);
    };
    requestAnimationFrame(loop);
  }
}
customElements.define('product-gallery-grid', productGalleryGrid);

class productGallerySlider extends HTMLElement {
  constructor() {
    super();
    this.videoItems = [];
  }
  connectedCallback() {    
    this.isOneImage = this.classList.contains('one-image');
    
    this.swiper = new Swiper(this, {
      slidesPerView: 1,
      spaceBetween: 0,
      mousewheel: {
          forceToAxis: true
      },
      pagination: {
        el: ".swiper-pagination",
        clickable: true,
      },
      breakpoints: {
        530: {
          slidesPerView: 2,
          spaceBetween: 10,
          centerInsufficientSlides: true,
        }
      }
    });
    const items = this.querySelectorAll('.product-gallery-item.has_video');

    items.forEach((item) => {
      const video = item.querySelector('video');
      const videoToggle = item.querySelector('.gallery-video-toggle');
      const videoSeekbar = item.querySelector('.gallery-video-seekbar');
      const volumeToggle = item.querySelector('.gallery-volume-toggle');

      if (!video || !videoToggle || !videoSeekbar || !volumeToggle) return;

      const itemData = { item, video, videoToggle, videoSeekbar, volumeToggle };
      this.videoItems.push(itemData);
      this.bindEvents(itemData);
    });

    this.startSeekbarLoop();

  }
  bindEvents({ video, videoToggle, videoSeekbar, volumeToggle }) {
    videoToggle.addEventListener('click', () => this.togglePlayback(video, videoToggle));
    video.addEventListener('ended', () => this.resetPlaybackButton(videoToggle));
    this.swiper.on('slideChange', () => {
      this.pauseVideo(video, videoToggle);
    });

    videoSeekbar.addEventListener('input', () =>
      this.seekVideo(video, videoSeekbar)
    );

    volumeToggle.addEventListener('click', () => {
      video.muted = !video.muted;
      volumeToggle.classList.toggle('muted', video.muted);
    });
  }

  togglePlayback(video, button) {
    if (video.paused || video.ended) {
      video.play();
      button.textContent = 'Pause';
    } else {
      video.pause();
      button.textContent = 'Play';
    }
  }

  resetPlaybackButton(button) {
    button.textContent = 'Play';
  }

  seekVideo(video, seekbar) {
    const time = (seekbar.value / 100) * video.duration;
    video.currentTime = time;
    this.updateSeekbar(video, seekbar);
  }

  updateSeekbar(video, seekbar) {
    const progress = (video.currentTime / video.duration) * 100;
    seekbar.value = progress;
    seekbar.style.setProperty('--progress', `${progress}%`);
  }

  pauseVideo(video, button) {
    if (!video.paused) {
      video.pause();
      button.textContent = 'Play';
    }      
  }

  startSeekbarLoop() {
    const loop = () => {
      this.videoItems.forEach(({ video, videoSeekbar }) => {
        if (!video.paused && !video.ended) {
          this.updateSeekbar(video, videoSeekbar);
        }
      });
      requestAnimationFrame(loop);
    };
    requestAnimationFrame(loop);
  }
}
customElements.define("product-gallery-slider", productGallerySlider);

class multiProductsSelector extends HTMLElement {
  constructor() {
    super();
    this.hasRevealed = false;
  }

  connectedCallback() {
    this.initCards();
    const firstCard = this.cards?.[0];
    const hasToggle = firstCard?.querySelector('.mpb-toggle');

    if (hasToggle) {
      this.observeVisibility();
    }
    this.initCheckboxTracking();

    // on resize, update card heights
    window.addEventListener('resize', () => {
      this.cards.forEach(card => {
        const content = card.querySelector('.mpb-product-item-body');
        if (content) {
          card.style.setProperty('--card-height', `${content.scrollHeight}px`);
          if (card.classList.contains('open')) {
            content.style.maxHeight = `${content.scrollHeight}px`;
          }
        }
      });
    });
  }

  initCards() {
    this.cards = this.querySelectorAll('.mpb-product-item');
    this.cards.forEach(card => this.setupCard(card));
  }

  setupCard(card) {
    const trigger = card.querySelector('.mpb-toggle');
    const content = card.querySelector('.mpb-product-item-body');

    card.style.setProperty('--card-height', `${content.scrollHeight}px`);

    if (!trigger || !content) return;

    trigger.addEventListener('click', () => this.toggleCard(card, trigger, content));
    content.addEventListener('click', () => this.toggleCard(card, trigger, content));
  }

  toggleCard(card, trigger, content) {
    const isOpen = card.classList.contains('open');
    card.classList.toggle('open', !isOpen);
    trigger.setAttribute('aria-expanded', String(!isOpen));
    content.style.maxHeight = !isOpen ? `${content.scrollHeight}px` : null;
  }

  observeVisibility() {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.hasRevealed) {
          this.hasRevealed = true;
          setTimeout(() => {
            this.revealFirstCardTemporarily();
          }, 500);
          observer.disconnect();
        }
      });
    }, { threshold: 0.3 });

    observer.observe(this);
  }

  revealFirstCardTemporarily() {
    const firstCard = this.cards?.[0];
    if (!firstCard) return;

    const content = firstCard.querySelector('.mpb-product-item-body');
    if (!content) return;

    const cardHeight = content.scrollHeight;
    const marginHeight = -1 * cardHeight;

    const bouncePeak = marginHeight + 30;

    const animationName = `bounce_dynamic_${Date.now()}`;

    // Create a keyframes rule string
    const keyframes = `
      @keyframes ${animationName} {
        0%, 20%, 50%, 80%, 100% {margin-bottom: ${marginHeight}px;}
        40% {margin-bottom: ${bouncePeak}px;}
        60% {margin-bottom: ${bouncePeak}px}
      }
    `;

    // Inject the keyframes into a new <style> tag
    const style = document.createElement('style');
    style.textContent = keyframes;
    document.head.appendChild(style);

    // Apply animation to content
    content.style.animation = `${animationName} 1.5s ease-in-out`;

    // Clean up the animation name and style tag after it's done
    setTimeout(() => {
      content.style.animation = '';
      style.remove();
    }, 2000);
  }

  initCheckboxTracking() {
    this.button = this.querySelector('.mpb-products-cta');
    this.buttonSpan = this.button.querySelector('span');
    this.checkboxes = this.querySelectorAll('input[type="checkbox"]');

    if (!this.button || !this.checkboxes.length) return;

    this.updateButtonLabel(); // set initial label

    this.checkboxes.forEach(checkbox => {
      checkbox.addEventListener('change', () => this.updateButtonLabel());
      checkbox.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          checkbox.checked = !checkbox.checked;
          checkbox.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    });

    this.button.addEventListener('click', () => this.handleAddToCart());
  }

  updateButtonLabel() {
    const checkedCount = [...this.checkboxes].filter(cb => cb.checked).length;

    if (checkedCount > 0) {
      this.buttonSpan.textContent = `Add ${checkedCount} item${checkedCount > 1 ? 's' : ''} to Bag`;
      this.button.setAttribute('aria-label', `Add ${checkedCount} item${checkedCount > 1 ? 's' : ''} to Bag`);
      this.button.classList.remove('no-hover');
    } else {
      this.buttonSpan.textContent = 'Select Products';
      this.button.setAttribute('aria-label', 'Select Products');
      this.button.classList.add('no-hover');
    }
  }

  getCheckedVariantItems() {
    const checkedCards = [...this.cards].filter(card =>
      card.querySelector('input[type="checkbox"]')?.checked
    );

    return checkedCards
      .map(card => {
        const variantId = card.getAttribute('data-variant-id');
        return variantId ? { id: variantId, quantity: 1 } : null;
      })
      .filter(Boolean);
  }

  async handleAddToCart() {
    const items = this.getCheckedVariantItems();
    if (!items.length) return;
    this.button.classList.add('loading');
    this.button.querySelector('.loading__spinner')?.classList.remove('hidden');

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ items })
      });

      const result = await response.json();
      this.button.classList.remove('loading');
      this.button.querySelector('.loading__spinner')?.classList.add('hidden');

      refreshCartDrawer();

    } catch (err) {
      console.error('Cart add failed:', err);
    }
  }
}
customElements.define('multi-products-selector', multiProductsSelector);

function refreshCartDrawer() {
  fetch("/cart?view=drawer")
    .then((response) => {
      if (!response.ok) throw new Error("Network response was not ok");
      return response.text();
    })
    .then((cartHtml) => {
      const parser = new DOMParser();
      const newDocument = parser.parseFromString(cartHtml, "text/html");

      const cartDrawer = document.querySelector("cart-drawer");
      const newCartContent = newDocument.querySelector("cart-drawer");

      const cartCount = document.querySelector("#cart-icon-bubble");
      const newCartCount = newDocument.querySelector("#cart-icon-bubble");

      if (cartDrawer && newCartContent) {
        cartDrawer.querySelector(".drawer__inner").innerHTML = newCartContent.querySelector(".drawer__inner").innerHTML;
        cartDrawer.className = newCartContent.className;
        cartDrawer.open();
      }

      if (cartCount && newCartCount) {
        cartCount.innerHTML = newCartCount.innerHTML;
      }
    })
    .catch((error) => {
      console.error("Error refreshing cart drawer:", error);
    })
    .finally(() => {
      console.log("Cart Drawer refreshed");
    });
}

class testimonialQuotesSlider extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
      this.hasAutoplay = this.dataset.autoplay === "true";

      this.swiper = new Swiper(this, {
        slidesPerView: 1,
        spaceBetween: 0,
        effect: "fade",
        loop: true,
        fadeEffect: {
          crossFade: true
        },
        autoplay: this.hasAutoplay ? {
          delay: this.dataset.autoplayDuration,
          disableOnInteraction: false
        } : false,
        mousewheel: {
          forceToAxis: true
        }
      });
  }
}
customElements.define("testimonial-quotes-slider", testimonialQuotesSlider);

// Toggle product description visibility
function hideTrimmedDesc(element){
  const trimmedDesc = element.closest('.product-description-trimmed');
  const fullDesc = trimmedDesc.nextElementSibling;
  if (trimmedDesc && fullDesc) {
    trimmedDesc.classList.add('hidden');
    fullDesc.classList.remove('hidden');
    fullDesc.querySelector(".toggle_description").focus();
  }
}

function hideFullDesc(element){
  const fullDesc = element.closest('.product-description-full');
  const trimmedDesc = fullDesc.previousElementSibling;
  if (trimmedDesc && fullDesc) {
    trimmedDesc.classList.remove('hidden');
    fullDesc.classList.add('hidden');
    trimmedDesc.querySelector(".toggle_description").focus();
  }
}

class FaqBlocks extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.initAccordions();
  }

  initAccordions() {
    const accordions = this.querySelectorAll('.faq-block');

    accordions.forEach(accordion => {
      const title = accordion.querySelector('.faq-block-title');
      const button = title.querySelector('.faq-block-trigger');
      const content = accordion.querySelector('.faq-answer');
      const isOpen = accordion.classList.contains('open');

      this.setup(button, content, isOpen);

      button.setAttribute('role', 'button');
      button.setAttribute('tabindex', '0');

      if (!button.dataset.accordionBound) {
        const toggleAccordion = () => {
          // Close other accordions inside the same component
          accordions.forEach(other => {
            if (other !== accordion && other.classList.contains('open')) {
              const otherButton = other.querySelector('.faq-block-trigger');
              const otherContent = other.querySelector('.faq-answer');
              other.classList.remove('open');
              this.setup(otherButton, otherContent, false);
            }
          });

          // Toggle this one
          const nowOpen = accordion.classList.toggle('open');
          this.setup(button, content, nowOpen);
        };

        title.addEventListener('click', toggleAccordion);
        button.addEventListener('keydown', e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggleAccordion();
          }
        });

        button.dataset.accordionBound = 'true';
      }
    });
  }

  setup(button, content, isOpen) {
    button.setAttribute('aria-expanded', isOpen ? 'true' : 'false');

    if (isOpen) {
      content.removeAttribute('inert');
      content.style.maxHeight = content.scrollHeight + 'px';
    } else {
      content.setAttribute('inert', '');
      content.style.maxHeight = '0';
    }
  }
}
customElements.define('faq-blocks', FaqBlocks);

class cartUpsell extends HTMLElement {
  constructor() {
      super();
  }
  connectedCallback() {
    this.isopen = this.closest('cart-drawer')?.getAttribute('upsell-open') === 'true' || false;

    this.swiper = new Swiper(this.querySelector('.cart-upsell-slider'), {
      slidesPerView: "auto",
      spaceBetween: 15,
      freeMode: true,
      mousewheel: {
          forceToAxis: true
      },
      grabCursor: true,
    });

    this.closeButton = this.querySelector('.cart-upsell-button');
    if (!this.isopen) {
      this.classList.toggle('collapsed');
      this.classList.add('close');
      setTimeout(() => {
        this.classList.remove('close');
      }, 100);
      this.closeButton.setAttribute('aria-expanded', this.classList.contains('collapsed') ? 'false' : 'true');
      this.querySelector('.cart-upsell-slider').toggleAttribute('inert');
    }
    this.closeButton.addEventListener('click', () => {
      this.classList.toggle('collapsed');
      this.closest('cart-drawer')?.setAttribute('upsell-open', this.classList.contains('collapsed') ? 'false' : 'true');
      this.closeButton.setAttribute('aria-expanded', this.classList.contains('collapsed') ? 'false' : 'true');
      this.querySelector('.cart-upsell-slider').toggleAttribute('inert');
    });
  }
}
customElements.define("cart-upsell", cartUpsell);

window.addEventListener("scroll", () => {
  if(window.innerWidth < 1025) return;
  const scrolled = window.scrollY + window.innerHeight;
  const totalHeight = document.documentElement.scrollHeight;
  const el = document.body;

  if (el) {
    if (scrolled >= totalHeight - 40) {
      el.classList.add("at-bottom");
    } else {
      el.classList.remove("at-bottom");
    }
  }
});
